import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { Subject, takeUntil } from 'rxjs';
import dayjs from 'dayjs';
import { DashboardService } from '@lmc-app/core/services/dashboard.service';
import { AppsConversionData } from '@lmc-app/shared/interface/apps-conversion-data';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { CUSTOM_DATE_FORMAT } from '@lmc-app/shared/constants/common.constant';

/** Auto-refresh interval: 5 minutes. */
const REFRESH_INTERVAL_MS = 300000;

@Component({
	selector: 'app-apps-conversion',
	templateUrl: './apps-conversion.component.html',
	styleUrls: ['./apps-conversion.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatCardModule,
		MatDatepickerModule,
		MatFormFieldModule,
		MatInputModule,
		MatProgressSpinnerModule,
		MatSnackBarModule,
		MatSortModule,
		MatTableModule,
	],
	providers: [
		{
			provide: DateAdapter,
			useClass: MomentDateAdapter,
			deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
		},
		{ provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMAT },
	],
})
export class AppsConversionComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild(MatSort) sort!: MatSort;

	readonly displayedColumns = ['agent', 'appsCreated', 'appsConverted', 'convertedNotCreated'];
	readonly dataSource = new MatTableDataSource<AppsConversionData>([]);

	readonly dateRange = new FormGroup({
		start: new FormControl<Date | null>(new Date()),
		end: new FormControl<Date | null>(new Date()),
	});

	isLoading = false;

	private refreshTimer?: ReturnType<typeof setInterval>;
	private readonly destroy$ = new Subject<void>();

	constructor(
		private dashboardService: DashboardService,
		private activatedRoute: ActivatedRoute,
		private snackBar: MatSnackBar
	) {}

	ngOnInit(): void {
		const { startDate, endDate } = this.activatedRoute.snapshot.queryParams;
		if (startDate) this.dateRange.controls.start.setValue(dayjs(startDate).toDate());
		if (endDate) this.dateRange.controls.end.setValue(dayjs(endDate).toDate());

		this.loadData();
		this.refreshTimer = setInterval(() => this.loadData(), REFRESH_INTERVAL_MS);
	}

	ngAfterViewInit(): void {
		this.dataSource.sort = this.sort;
	}

	ngOnDestroy(): void {
		clearInterval(this.refreshTimer);
		this.destroy$.next();
		this.destroy$.complete();
	}

	/** Reloads once both ends of the range are picked. */
	onRangeChange(): void {
		const { start, end } = this.dateRange.value;
		if (start && end) this.loadData();
	}

	loadData(): void {
		const { start, end } = this.dateRange.value;
		if (!start || !end) return;

		this.isLoading = true;
		this.dashboardService
			.getAppsConversion(dayjs(start).format('YYYY-MM-DD'), dayjs(end).format('YYYY-MM-DD'))
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (data: AppsConversionData[]) => {
					this.dataSource.data = data;
					this.isLoading = false;
				},
				error: (error) => {
					console.error(error);
					this.isLoading = false;
					this.snackBar.open(
						error.error?.message || 'Failed to load Apps Conversion data',
						SnackbarConstants.ACTION_DISMISS,
						SnackbarConstants.DURATION_DISMISS_SHORT
					);
				},
			});
	}
}
